import { useState } from 'react'
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from 'lucide-react'
import AnimatedSection from '@/components/AnimatedSection'

const serviceTypes = [
  'Topographic Survey',
  'Boundary Survey',
  'Construction Survey',
  'GIS Services',
  'Drone Mapping',
  'Cadastral Survey',
  'Other',
]

export default function Contact() {
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      const response = await fetch('/api/messages/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })
      const data = await response.json().catch(() => ({}))

      if (!response.ok) throw new Error(data.error || 'Unable to send your message. Please try again.')
      if (data.warning) throw new Error('Your message could not be saved. Please try again later.')

      setSubmitted(true)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Unable to send your message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return (
    <div className="min-h-screen pt-32">
      {/* Hero */}
      <section className="py-20 bg-cream">
        <div className="section-padding max-w-7xl mx-auto text-center">
          <AnimatedSection>
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Contact</span>
            <h1 className="heading-lg text-black mt-3 mb-4 font-serif">Get In Touch</h1>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Ready to start your project? Contact us for a free consultation and quote.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="section-padding max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <AnimatedSection>
              {submitted ? (
                <div className="bg-cream rounded-2xl p-12 text-center">
                  <CheckCircle2 className="w-16 h-16 text-accent mx-auto mb-6" />
                  <h2 className="heading-sm text-black mb-4 font-serif">Message Sent!</h2>
                  <p className="text-neutral-600 mb-6">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', service_type: '', message: '' }) }}
                    className="btn-primary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <h2 className="heading-sm text-black mb-6 font-serif">Send Us a Message</h2>
                  {submitError && <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{submitError}</p>}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                        placeholder=" +234 703 196 8859"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Service Type</label>
                      <select
                        name="service_type"
                        required
                        value={formData.service_type}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all bg-white"
                      >
                        <option value="">Select a service</option>
                        {serviceTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Message *</label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all resize-none"
                      placeholder="Tell us about your project..."
                    />
                  </div>
                  <button type="submit" disabled={isSubmitting} className="btn-primary w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-60">
                    <Send className="w-4 h-4" />
                    {isSubmitting ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
              )}
            </AnimatedSection>

            {/* Contact Info */}
            <AnimatedSection delay={0.2}>
              <div className="space-y-8">
                <div>
                  <h2 className="heading-sm text-black mb-6 font-serif">Contact Information</h2>
                  <p className="text-neutral-600 mb-8">
                    Reach out to us through any of the channels below. Our team is available
                    Monday through Saturday to assist you.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-accent-dark" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black mb-1 font-serif">Office Address</h3>
                      <p className="text-neutral-600 text-sm">
                        11B, Town Planning Way, Federal Low Cost Housing Estate,<br />
                        Ipaja, Alimosho L.G., Lagos
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-accent-dark" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black mb-1 font-serif">Phone</h3>
                      <p className="text-neutral-600 text-sm"> +234 703 196 8859</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-accent-dark" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black mb-1 font-serif">Email</h3>
                      <p className="text-neutral-600 text-sm">advictcares@yahoo.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-accent-dark" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black mb-1 font-serif">Office Hours</h3>
                      <p className="text-neutral-600 text-sm">Monday - Friday: 8:00 AM - 5:00 PM</p>
                      <p className="text-neutral-600 text-sm">Saturday: 9:00 AM - 2:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-24 bg-surface">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection>
            <div className="rounded-2xl overflow-hidden shadow-lg border border-neutral-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.7!2d3.4!3d6.45!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMjcnMDAuMCJOIDPCsDI0JzAwLjAiRQ!5e0!3m2!1sen!2sng!4v1"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Advict Spatial Consult Office Location"
              />
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  )
}
